/**
 * 앱 업데이트 확인 유틸리티
 * 
 * 현재 버전과 서버의 최신 버전을 비교하여 업데이트 여부를 확인합니다.
 */
import { APP_VERSION, BUILD_DATE } from '../version.js';
import { showToast } from './ui-manager.js';

// 현재 버전 정보
const currentVersion = APP_VERSION;
const buildDate = new Date(BUILD_DATE);

/**
 * 서버에서 최신 버전 정보를 가져옵니다.
 * 실제 구현에서는 서버 API를 호출해야 합니다.
 * 
 * @returns {Promise<{version: string, date: string, notes: string}>} 최신 버전 정보
 */
async function fetchLatestVersion() {
  try {
    // 실제 서비스에서는 아래 주석을 해제하고 실제 API 엔드포인트를 사용해야 합니다.
    // const response = await fetch('https://api.example.com/version');
    // return await response.json();
    
    // 개발용 모의 응답 (실제 구현 시 삭제)
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          version: '1.0.15', // 현재 버전과 동일하게 설정 (테스트용)
          date: '2025-04-24T22:51:45.561Z',
          notes: '버그 수정 및 성능 개선',
          changeLog: [
            '댓글 알림 기능 추가',
            '이미지 로딩 속도 개선',
            '채팅 UI 개선',
            'Firebase 연동 안정성 강화'
          ]
        });
      }, 500);
    });
  } catch (error) {
    console.error('버전 정보를 가져오는 중 오류 발생:', error);
    throw new Error('버전 정보를 가져올 수 없습니다.');
  }
}

/**
 * 버전 문자열을 숫자 배열로 변환합니다.
 * 예: "1.0.15" -> [1, 0, 15]
 * 
 * @param {string} version - 버전 문자열
 * @returns {number[]} 숫자 배열로 변환된 버전
 */
function parseVersion(version) {
  return version.split('.').map(Number);
}

/**
 * 두 버전을 비교합니다.
 * 
 * @param {string} currentVersion - 현재 버전
 * @param {string} latestVersion - 최신 버전
 * @returns {number} currentVersion이 더 크면 1, 같으면 0, 작으면 -1
 */
function compareVersions(currentVersion, latestVersion) {
  const current = parseVersion(currentVersion);
  const latest = parseVersion(latestVersion);
  
  for (let i = 0; i < Math.max(current.length, latest.length); i++) {
    const a = current[i] || 0;
    const b = latest[i] || 0;
    
    if (a > b) return 1;
    if (a < b) return -1;
  }
  
  return 0;
}

/**
 * 앱 업데이트 확인 함수
 * 
 * @param {boolean} showNoUpdateMessage - 업데이트가 없을 때도 메시지를 표시할지 여부
 * @returns {Promise<{hasUpdate: boolean, latestVersion: string, notes: string}>} 업데이트 정보
 */
export async function checkForUpdates(showNoUpdateMessage = true) {
  try {
    const latestVersionInfo = await fetchLatestVersion();
    const comparisonResult = compareVersions(currentVersion, latestVersionInfo.version);
    const hasUpdate = comparisonResult < 0;
    
    if (hasUpdate) {
      showUpdateAvailableModal(latestVersionInfo);
      return { 
        hasUpdate: true, 
        latestVersion: latestVersionInfo.version, 
        notes: latestVersionInfo.notes,
        changeLog: latestVersionInfo.changeLog 
      };
    } else {
      if (showNoUpdateMessage) {
        showToast('이미 최신 버전을 사용 중입니다.', 'info');
      }
      return { 
        hasUpdate: false, 
        latestVersion: latestVersionInfo.version,
        notes: ''
      };
    }
  } catch (error) {
    console.error('업데이트 확인 중 오류 발생:', error);
    showToast('업데이트 확인 중 오류가 발생했습니다.', 'error');
    return { hasUpdate: false, latestVersion: '', notes: '' };
  }
}

/**
 * 업데이트 가능 모달을 표시합니다.
 * 
 * @param {Object} versionInfo - 버전 정보
 */
function showUpdateAvailableModal(versionInfo) {
  const modalId = 'updateAvailableModal';
  let modalElement = document.getElementById(modalId);
  
  // 이미 모달이 존재하면 제거
  if (modalElement) {
    modalElement.remove();
  }
  
  // 변경 내역 목록 생성
  const changeLogItems = versionInfo.changeLog && versionInfo.changeLog.length > 0
    ? versionInfo.changeLog.map(item => `<li>${item}</li>`).join('')
    : '<li>버그 수정 및 성능 개선</li>';
  
  // 모달 HTML 생성
  const modalHTML = `
    <div class="modal fade" id="${modalId}" tabindex="-1" aria-labelledby="updateModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="updateModalLabel">
              <i class="bi bi-arrow-up-circle-fill text-primary me-2"></i>
              업데이트 가능
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <span>현재 버전:</span>
              <span class="badge bg-secondary">${currentVersion}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-4">
              <span>최신 버전:</span>
              <span class="badge bg-primary">${versionInfo.version}</span>
            </div>
            
            <h6 class="border-top pt-3">변경 내역:</h6>
            <ul class="mb-0">
              ${changeLogItems}
            </ul>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">닫기</button>
            <button type="button" class="btn btn-primary" id="updateNowBtn">
              <i class="bi bi-arrow-repeat me-1"></i>
              지금 업데이트
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
  
  // 모달을 body에 추가
  document.body.insertAdjacentHTML('beforeend', modalHTML);
  
  // 모달 요소 참조 가져오기
  modalElement = document.getElementById(modalId);
  
  // Bootstrap 모달 인스턴스 생성 및 표시
  const modal = new bootstrap.Modal(modalElement);
  modal.show();
  
  // 업데이트 버튼 이벤트 핸들러 추가
  document.getElementById('updateNowBtn').addEventListener('click', () => {
    modal.hide();
    
    // 브라우저 캐시를 무시하고 페이지 새로고침
    window.location.reload(true);
  });
}

/**
 * 주기적으로 업데이트를 확인합니다. (하루에 한 번)
 */
export function setupPeriodicUpdateCheck() {
  // 마지막 업데이트 확인 시간 가져오기
  const lastUpdateCheck = localStorage.getItem('lastUpdateCheck');
  const currentTime = new Date().getTime();
  
  // 하루(24시간)를 밀리초로 변환
  const oneDayInMs = 24 * 60 * 60 * 1000;
  
  // 지난 확인으로부터 하루가 지났거나 기록이 없으면 업데이트 확인
  if (!lastUpdateCheck || (currentTime - parseInt(lastUpdateCheck)) > oneDayInMs) {
    // 사용자에게 메시지 표시하지 않고 백그라운드에서 확인
    checkForUpdates(false).then(result => {
      if (result.hasUpdate) {
        showToast('새로운 업데이트가 있습니다.', 'info');
      }
    });
    
    // 현재 시간을 마지막 확인 시간으로 저장
    localStorage.setItem('lastUpdateCheck', currentTime.toString());
  }
} 